import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@pazportal/ui';
import { HEALTH_SIGNAL_KEYS, type HealthSignalKey } from '@pazportal/types';
import {
  useAllCustomersWithSignals,
  describeSignal,
  SIGNAL_LABEL,
  type CustomerWithSignals,
} from '../hooks/useAllCustomersWithSignals';
import { usePlatformMetrics } from '../hooks/usePlatformMetrics';
import { HealthPill } from '../components/HealthSignals';
import { formatCurrency, formatMrrCompact, formatDate, daysUntil } from '../lib/formatters';

/** The weakest signal that actually has data, for the churn-risk reason line. */
function weakestSignal(c: CustomerWithSignals): HealthSignalKey | null {
  let weakest: HealthSignalKey | null = null;
  for (const key of HEALTH_SIGNAL_KEYS) {
    const value = c.health.signals[key];
    if (value === null) continue;
    if (weakest === null || value < (c.health.signals[weakest] ?? Number.POSITIVE_INFINITY)) weakest = key;
  }
  return weakest;
}

export function RevenuePage() {
  const { data: customers, isLoading: custLoading, isError: custError } = useAllCustomersWithSignals();
  const { data: metrics, isLoading: metLoading, isError: metError } = usePlatformMetrics();
  const navigate = useNavigate();

  const totalMrr = metrics?.totalMrrCents ?? 0;
  const arr = totalMrr * 12;

  const churnRisk = useMemo(() => {
    return (customers ?? []).filter((c) => {
      const days = daysUntil(c.contract_renewal_date);
      return days !== null && days <= 30 && c.health.signals.status === 'critical';
    });
  }, [customers]);

  const renewalsIn60 = useMemo(() => {
    return (customers ?? []).filter((c) => {
      const days = daysUntil(c.contract_renewal_date);
      return days !== null && days <= 60 && days > 0;
    });
  }, [customers]);

  const upcomingRenewals = useMemo(() => {
    return (customers ?? [])
      .filter((c) => c.contract_renewal_date)
      .sort((a, b) => new Date(a.contract_renewal_date!).getTime() - new Date(b.contract_renewal_date!).getTime())
      .slice(0, 20);
  }, [customers]);

  if (custLoading || metLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading revenue</span>
      </div>
    );
  }

  if (custError || metError) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-sm text-notification" role="alert">
        Could not load revenue data. Refresh to try again.
      </div>
    );
  }

  const unknownHealthWithRenewal = (customers ?? []).filter((c) => {
    const days = daysUntil(c.contract_renewal_date);
    return days !== null && days <= 30 && c.health.signals.status === 'unknown';
  }).length;

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold text-text">Revenue</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MetricCard label="Total MRR" value={formatMrrCompact(totalMrr)} />
        <MetricCard label="ARR (MRR × 12)" value={formatMrrCompact(arr)} />
        <MetricCard label="Renewals in 60d" value={String(renewalsIn60.length)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-surface rounded-lg border border-border p-4">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Churn Risk</h2>
          <p className="text-xs text-text-muted mb-3">Customers renewing within 30 days whose health is critical.</p>
          {churnRisk.length === 0 ? (
            <p className="text-sm text-text-muted">
              No customers at churn risk.
              {unknownHealthWithRenewal > 0
                ? ` ${unknownHealthWithRenewal} renewing within 30 days ${unknownHealthWithRenewal === 1 ? 'has' : 'have'} no health data yet.`
                : ''}
            </p>
          ) : (
            <div className="space-y-1.5">
              {churnRisk.map((c) => {
                const days = daysUntil(c.contract_renewal_date);
                const weakest = weakestSignal(c);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => navigate(`/customers/${c.id}`)}
                    className="w-full text-left p-2.5 min-h-[44px] rounded-md border border-notification bg-notification-soft hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-text truncate">{c.name}</p>
                      <HealthPill health={c.health} showLabel={false} />
                    </div>
                    <p className="text-xs text-text-muted mt-0.5">
                      {formatCurrency(c.mrr_cents)} MRR · {days}d to renewal
                      {weakest ? ` · ${SIGNAL_LABEL[weakest]}: ${describeSignal(weakest, c.health)}` : ''}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-surface rounded-lg border border-border p-4">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">About these numbers</h2>
          <p className="text-sm text-text-muted">
            Total MRR is the live sum of each customer’s current MRR. There is no monthly history table, so this page does not show a trend or per-solution averages. Health scores come from open tickets, SLA deadlines, renewal dates and network telemetry where it exists.
          </p>
        </div>
      </div>

      <div className="bg-surface rounded-lg border border-border p-4">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Upcoming Renewals</h2>
        {upcomingRenewals.length === 0 ? (
          <EmptyState
            icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3M3 11h18M5 7h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2z" /></svg>}
            title="No upcoming renewals"
            subtitle="Customers with a contract renewal date will appear here."
          />
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              {upcomingRenewals.map((c) => {
                const days = daysUntil(c.contract_renewal_date);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => navigate(`/customers/${c.id}`)}
                    className="w-full text-left rounded-lg border border-border bg-surface-2 p-3 min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-text truncate">{c.name}</p>
                      <HealthPill health={c.health} showLabel={false} />
                    </div>
                    <p className="text-xs text-text-muted mt-1">
                      {formatCurrency(c.mrr_cents)} MRR · {formatRenewalDate(c.contract_renewal_date)} · <span className={dayTone(days)}>{days === null ? '—' : `${days}d`}</span>
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-text-muted">
                    <th className="pb-2 font-medium">Customer</th>
                    <th className="pb-2 font-medium text-right">MRR</th>
                    <th className="pb-2 font-medium text-center">Renewal</th>
                    <th className="pb-2 font-medium text-center">Days</th>
                    <th className="pb-2 font-medium text-center">Health</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {upcomingRenewals.map((c) => {
                    const days = daysUntil(c.contract_renewal_date);
                    return (
                      <tr
                        key={c.id}
                        className="hover:bg-surface-strong cursor-pointer"
                        onClick={() => navigate(`/customers/${c.id}`)}
                      >
                        <td className="py-2 text-text font-medium">{c.name}</td>
                        <td className="py-2 text-right text-text">{formatCurrency(c.mrr_cents)}</td>
                        <td className="py-2 text-center text-xs text-text-muted">{formatRenewalDate(c.contract_renewal_date)}</td>
                        <td className={`py-2 text-center ${dayTone(days)}`}>{days ?? '—'}</td>
                        <td className="py-2 text-center"><HealthPill health={c.health} showLabel={false} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function dayTone(days: number | null): string {
  if (days === null) return 'text-text-muted';
  if (days <= 30) return 'text-notification font-semibold';
  if (days <= 60) return 'text-warning';
  return 'text-text';
}

/** contract_renewal_date is a DATE: formatDate reads it as a local calendar day. */
function formatRenewalDate(date: string | null): string {
  return formatDate(date);
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface rounded-lg border border-border p-4">
      <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-semibold mt-1 text-text">{value}</p>
    </div>
  );
}
